import { useMemo, useRef } from 'react'
import { Download, FileDown } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/DataTable'
import { Button } from '@/components/ui/button'
import { useReportBrand } from '@/lib/pdf'
import type { ChatMessagePart } from '../types'
import { downloadPdfFromElementTable } from '../export'
import { ShareButton } from './actionBar'

type TablePart = Extract<ChatMessagePart, { kind: 'table' }>

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return value.toString()
    return value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function downloadCsv(columns: TablePart['columns'], rows: TablePart['rows'], filenameStub: string) {
  const header = columns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(',')
  const body = rows
    .map((r) =>
      columns
        .map((c) => `"${formatCell(r[c.key]).replace(/"/g, '""')}"`)
        .join(','),
    )
    .join('\n')
  const csv = `${header}\n${body}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${filenameStub}.csv`
  link.click()
  URL.revokeObjectURL(link.href)
}

interface Props {
  part: TablePart
  onShare?: () => void
}

export function TableBlock({ part, onShare }: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const title = part.title || 'Table'
  const brand = useReportBrand()
  const columns = useMemo<ColumnDef<Record<string, unknown>, unknown>[]>(
    () =>
      part.columns.map((c) => ({
        id: c.key,
        accessorKey: c.key,
        header: () => <span className={c.align === 'right' ? 'text-right' : undefined}>{c.label}</span>,
        cell: ({ getValue }) => {
          const raw = getValue() as unknown
          return <span className={c.align === 'right' ? 'block text-right' : undefined}>{formatCell(raw)}</span>
        },
      })),
    [part.columns],
  )

  return (
    <div className="my-3 flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {part.title ? (
          <p className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {part.title}
          </p>
        ) : <span />}
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => void downloadPdfFromElementTable(wrapperRef.current, title, brand)}
            className="size-7"
            title="Download PDF"
            aria-label="Download PDF"
          >
            <FileDown className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() =>
              downloadCsv(part.columns, part.rows, title.replace(/[^\w-]+/g, '_'))
            }
            className="size-7"
            title="Download CSV"
            aria-label="Download CSV"
          >
            <Download className="size-3.5" />
          </Button>
          <ShareButton onClick={onShare} />
        </div>
      </div>
      <div ref={wrapperRef} className="overflow-hidden rounded-lg border border-border">
        <DataTable
          columns={columns}
          rows={part.rows}
          sortable
          empty={<div className="py-6 text-center text-xs text-muted-foreground">No rows.</div>}
        />
      </div>
    </div>
  )
}
